import React, {useEffect, useRef, useState} from 'react'
import styled from 'styled-components'
import RouteContainer from '../../../common/styles/RouteContainer'
import useLibraryByBookCount from '../useLibraryByBookCount'
import RouteTitle from '../../../common/RouteTitle/RouteTitle'
import booksitoutIcon from '../../../config/BooksitoutIcon'
import RouteTitleConfig from '../../../config/RouteTitleConfig'
import LibraryCardLoading from '../find/LibraryCardLoading'
import LibraryCard from '../find/LibraryCard'
import RowSpacer from '../../../common/styles/RowSpacer'
import InfiniteScroll from 'react-infinite-scroll-component'
import {useNavigate} from 'react-router-dom'
import {Form} from 'react-bootstrap'
import useUrlQuery from '../../../common/hooks/useUrlQuery'
import toast from 'react-hot-toast'
import useCurrentLocation from '../useCurrentLocation'
import useLocationStore from "../useLocationStore";
import ColorConfig from "../../../config/ColorConfig";
import NoContent from '../../../common/NoContent'

const LibraryRankingRoute = () => {
	const location = useUrlQuery('location')
	const [currentRegion, setCurrentRegion] = useState<string>(location ?? 'ALL')
	const {isLocationActive} = useLocationStore()
	const [lat, long, locationName, isLocationLoading, locationError, refreshLocation] = useCurrentLocation(isLocationActive)

	const [libraries, isLoading, isError, paging] = useLibraryByBookCount(lat, long, currentRegion, 100)

	const navigate = useNavigate()
	const mapRef = useRef<HTMLDivElement | null>(null)
	const mapInstance = useRef<naver.maps.Map | null>(null)
	const clusteringRef = useRef<any>(null)

	useEffect(() => {
		const {naver} = window
		if (!mapRef.current || !naver) return

		if (!mapInstance.current) {
			mapInstance.current = new naver.maps.Map(mapRef.current, {
				zoom: 7,
				center: new naver.maps.LatLng(36.5, 127.5),
				zoomControl: false,
				logoControl: false,
				mapDataControl: false,
				mapTypeControl: false,
				scaleControl: false,
			})
		}

		if (libraries.length === 0) return

		if (clusteringRef.current) {
			clusteringRef.current.setMap(null)
			clusteringRef.current = null
		}

		const markers = libraries
			.filter(lib => lib.location?.latitude && lib.location?.longitude)
			.map(lib => {
				const marker = new naver.maps.Marker({
					position: new naver.maps.LatLng(lib.location.latitude, lib.location.longitude),
					draggable: false,
					title: lib.name,
					clickable: true,
					icon: {
						content: `<div style="
							width: 28px;
							height: 28px;
							background: ${ColorConfig.Primary};
							border-radius: 50% 50% 50% 0;
							transform: rotate(-45deg);
							border: 2px solid white;
							box-shadow: 0 2px 6px rgba(0,0,0,0.3);
						"></div>`,
						size: new naver.maps.Size(28, 28),
						anchor: new naver.maps.Point(14, 28),
					},
				})

				naver.maps.Event.addListener(marker, 'click', () => {
					navigate(`/library/${lib.id}`)
				})

				return marker
			})

		const makeClusterIcon = (bg: string) => ({
			content: `<div style="
				cursor:pointer;width:40px;height:40px;line-height:42px;
				font-size:12px;color:#fff;text-align:center;font-weight:700;
				border-radius:999px;background:${bg};box-shadow:0 2px 8px rgba(0,0,0,.25);
			"></div>`,
			size: new naver.maps.Size(40, 40),
			anchor: new naver.maps.Point(20, 20),
		})

		const clusterIcons = [
			makeClusterIcon(ColorConfig.Primary),
			makeClusterIcon('#0d8a47'),
			makeClusterIcon('#0a6e38'),
			makeClusterIcon('#075229'),
			makeClusterIcon('#04361a'),
		]

		clusteringRef.current = new (window as any).MarkerClustering({
			map: mapInstance.current,
			markers,
			minClusterSize: 2,
			maxZoom: 13,
			gridSize: 100,
			disableClickZoom: false,
			icons: clusterIcons,
			indexGenerator: [10, 50, 100, 300, 500],
			stylingFunction: (clusterMarker: any, count: number) => {
				const el = clusterMarker.getElement() as HTMLElement
				const circle = el.querySelector('div')
				if (circle) circle.textContent = String(count)
			},
		})
	}, [libraries, navigate])

	return (
		<RouteContainer>
			<RouteTitle
				icon={<booksitoutIcon.library />}
				title={'책 많은 도서관 순위'}
				subTitle={'책이 많은 순서대로 도서관을 둘러 볼 수 있어요'}
				currentKey={'library'}
				buttons={RouteTitleConfig.Library}
				rightUi={
					<>
						<RegionSelect currentRegion={currentRegion} setCurrentRegion={setCurrentRegion} />
					</>
				}
			/>

			<RowSpacer/>

			<MapContainer ref={mapRef} className="rounded" />

			<RowSpacer size={40} />

			{!isLoading && libraries.length === 0 ? (
				<NoContentCenter>
					<NoContent message={isError ? '도서관 정보를 불러올 수 없어요' : '아직 도서관 정보가 없어요'} />
				</NoContentCenter>
			) : (
				<InfiniteScroll next={paging.fetchNext} hasMore={paging.hasMore} dataLength={libraries.length} loader={<LibraryLoading size={4} />}>
					<LibraryContainer>
						{libraries.map(library => (
							<Col key={library.id}>
								<LibraryCard library={library} ranking={library.ranking} backgroundColorType={'card'} />
							</Col>
						))}
					</LibraryContainer>
				</InfiniteScroll>
			)}
		</RouteContainer>
	)
}

interface LibraryLoadingProps {
	size: number
}

const LibraryLoading: React.FC<LibraryLoadingProps> = ({ size }) => {
	return (
		<>
			{Array.from({ length: size }).map((_, i) => (
				<Col key={i}>
					<LibraryCardLoading />
				</Col>
			))}
		</>
	)
}

const RegionSelect = ({ currentRegion, setCurrentRegion }) => {
	const navigate = useNavigate()

	const regionMap = [
		['전체', 'ALL'],
		['서울', 'SEOUL'],
		['경기', 'GYEONGGIDO'],
		['인천', 'INCHEON'],
		['대전', 'DAEJEON'],
		['대구', 'DAEGU'],
		['부산', 'BUSAN'],
		['울산', 'ULSAN'],
		['광주', 'GWANGJU'],
		['세종', 'SEJONG'],
		['강원', 'GANGWON'],
		['충북', 'CHUNGCHEONGBUKDO'],
		['충남', 'CHUNGCHEONGNAMDO'],
		['전북', 'JEONBUK'],
		['전남', 'JEOLLANAMDO'],
		['경북', 'GYEONGSANGBUKDO'],
		['경남', 'GYEONGSANGNAMDO'],
		['제주', 'JEJU'],
	]

	const postfixMap = [
		['전체', '로'],
		['서울', '로'],
		['경기', '로'],
		['인천', '으로'],
		['대전', '으로'],
		['대구', '로'],
		['부산', '으로'],
		['울산', '으로'],
		['광주', '로'],
		['세종', '으로'],
		['강원', '으로'],
		['충북', '으로'],
		['충남', '으로'],
		['전북', '으로'],
		['전남', '으로'],
		['경북', '으로'],
		['경남', '으로'],
		['제주', '로'],
	]

	return (
		<Form>
			<Form.Select
				onChange={e => {
					const selectedRegion = regionMap.find(([_, apiName]) => apiName === e.target.value)?.[0] ?? '-'
					const postfix = postfixMap.find(([apiName]) => apiName === e.target.value)?.[1] ?? '로'
					toast.success(`지역을 ${selectedRegion}${postfix} 바꿨어요.`)

					setCurrentRegion(e.target.value)

					navigate(`/library/ranking?location=${e.target.value}`)
				}}
			>
				{regionMap.map(([displayName, apiName]) => (
					<option key={apiName} selected={apiName === currentRegion} value={apiName}>
						{displayName}
					</option>
				))}
			</Form.Select>
		</Form>
	)
}

const NoContentCenter = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 400px;
`

const MapContainer = styled.div`
	width: 100%;
	height: 450px;
	z-index: 1;
`

const LibraryContainer = styled.div.attrs({
	className: 'ps-2 pe-2',
})``

const Col = styled.div``

export default LibraryRankingRoute
